import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Pin, Plus, Trash2, Loader2, BookHeart, Sparkles, X, Edit3, Save, Crown, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_META = {
  fact:       { label: 'Fact',       cls: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
  preference: { label: 'Preference', cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  emotion:    { label: 'Emotion',    cls: 'bg-pink-500/15 text-pink-300 border-pink-500/30' },
  event:      { label: 'Event',      cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
};

export default function Memory() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('global'); // global | character
  const [memories, setMemories] = useState([]);
  const [characters, setCharacters] = useState([]);
  const [selectedCharId, setSelectedCharId] = useState('');
  const [loading, setLoading] = useState(true);
  const [addingOpen, setAddingOpen] = useState(false);
  const [newContent, setNewContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingContent, setEditingContent] = useState('');
  const [clearing, setClearing] = useState(false);
  const [userTier, setUserTier] = useState(null); // null = still loading

  useEffect(() => { fetchUserTier(); fetchCharacters(); }, []);
  useEffect(() => { if (userTier && userTier !== 'free') fetchMemories(); }, [tab, selectedCharId, userTier]);

  const fetchUserTier = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/user/subscription`, { withCredentials: true });
      setUserTier(data.tier || 'free');
    } catch { setUserTier('free'); }
  };

  const fetchCharacters = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/conversations`, { withCredentials: true });
      // Deduplicate by character
      const map = {};
      (data || []).forEach((c) => {
        if (c.character_id && !map[c.character_id]) {
          map[c.character_id] = {
            id: c.character_id,
            name: c.character_name || 'Character',
            avatar: c.character_avatar || '',
          };
        }
      });
      setCharacters(Object.values(map));
    } catch (err) {
      console.error('Failed to fetch characters:', err);
    }
  };

  const fetchMemories = async () => {
    setLoading(true);
    try {
      const params = {};
      if (tab === 'global') params.character_id = '';
      else if (tab === 'character' && selectedCharId) params.character_id = selectedCharId;
      const { data } = await axios.get(`${API_URL}/api/memories`, { params, withCredentials: true });
      setMemories(data.memories || []);
    } catch (err) {
      console.error('Failed to fetch memories:', err);
      setMemories([]);
    } finally {
      setLoading(false);
    }
  };

  const addMemory = async () => {
    if (!newContent.trim()) return;
    setSubmitting(true);
    try {
      const payload = { content: newContent.trim() };
      if (tab === 'character' && selectedCharId) payload.character_id = selectedCharId;
      await axios.post(`${API_URL}/api/memories`, payload, { withCredentials: true });
      setNewContent('');
      setAddingOpen(false);
      fetchMemories();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to add memory');
    } finally {
      setSubmitting(false);
    }
  };

  const togglePin = async (m) => {
    try {
      await axios.patch(`${API_URL}/api/memories/${m.id}`, { pinned: !m.pinned }, { withCredentials: true });
      fetchMemories();
    } catch (err) {
      console.error('Toggle pin failed:', err);
    }
  };

  const saveEdit = async (m) => {
    if (!editingContent.trim()) { setEditingId(null); return; }
    try {
      await axios.patch(`${API_URL}/api/memories/${m.id}`, { content: editingContent.trim() }, { withCredentials: true });
      setEditingId(null);
      fetchMemories();
    } catch (err) {
      console.error('Save edit failed:', err);
    }
  };

  const deleteOne = async (m) => {
    if (!window.confirm('Forget this memory? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/api/memories/${m.id}`, { withCredentials: true });
      fetchMemories();
    } catch (err) {
      console.error('Delete memory failed:', err);
    }
  };

  const clearAll = async () => {
    if (!window.confirm('Clear all non-pinned memories in this view? Pinned memories will be kept.')) return;
    setClearing(true);
    try {
      const params = {};
      if (tab === 'global') params.character_id = '';
      else if (tab === 'character' && selectedCharId) params.character_id = selectedCharId;
      await axios.delete(`${API_URL}/api/memories`, { params, withCredentials: true });
      fetchMemories();
    } finally {
      setClearing(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-[#D4D4D8] pb-24 lg:pb-12">
      <div className="max-w-4xl mx-auto">
      {/* Free user → full-screen paywall (replaces the entire vault UI) */}
      {userTier === 'free' && (
        <>
          <header className="sticky top-0 z-30 bg-[#050505]/95 backdrop-blur-sm border-b border-white/5 px-4 h-12 flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="text-white" data-testid="memory-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2 flex-1">
              <BookHeart className="w-4 h-4 text-[#E11D48]" />
              <h1 className="font-bold text-white font-['Unbounded'] text-sm">Memory Vault</h1>
              <Lock className="w-3.5 h-3.5 text-[#F59E0B]" />
            </div>
          </header>

          <div className="px-5 pt-10 pb-8 max-w-md mx-auto" data-testid="memory-locked-view">
            <div className="relative">
              <div className="absolute -top-10 -right-10 w-56 h-56 bg-[#E11D48]/15 blur-3xl rounded-full pointer-events-none" />
              <div className="absolute -bottom-10 -left-10 w-56 h-56 bg-[#F59E0B]/10 blur-3xl rounded-full pointer-events-none" />

              <div className="relative">
                <div className="w-20 h-20 mx-auto rounded-3xl bg-gradient-to-br from-[#E11D48] to-[#9F1239] flex items-center justify-center mb-6 shadow-2xl shadow-[#E11D48]/30">
                  <BookHeart className="w-10 h-10 text-white" />
                </div>

                <h2 className="font-['Unbounded'] text-2xl text-white text-center mb-2 leading-tight">
                  Permanent Memory
                </h2>
                <p className="text-sm text-[#A1A1AA] text-center leading-relaxed mb-8 px-2">
                  Let your characters remember every detail about you — across every chat, forever. A core Premium feature.
                </p>

                {/* Tier cards */}
                <div className="space-y-3 mb-6">
                  <div className="p-4 rounded-2xl bg-gradient-to-br from-[#2A1A03] via-[#7C2D12]/60 to-[#F59E0B]/15 border border-[#F59E0B]/40">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Crown className="w-4 h-4 text-[#F59E0B]" />
                        <span className="text-sm font-bold text-white">Premium</span>
                      </div>
                      <span className="text-[10px] uppercase tracking-widest text-[#F59E0B] font-bold">Basic</span>
                    </div>
                    <p className="text-xs text-[#FCD34D]/90 leading-relaxed">
                      Up to 5 permanent memories your characters never forget.
                    </p>
                  </div>

                  <div className="p-4 rounded-2xl bg-gradient-to-br from-[#1B0E2E] via-[#4C1D95]/60 to-[#E11D48]/20 border border-[#E11D48]/50">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-[#FDA4AF]" />
                        <span className="text-sm font-bold text-white">Ultimate</span>
                      </div>
                      <span className="text-[10px] uppercase tracking-widest text-[#FDA4AF] font-bold">Flawless</span>
                    </div>
                    <p className="text-xs text-[#FECDD3]/90 leading-relaxed">
                      Unlimited memories. Best extraction AI. 3x deeper analysis every turn.
                    </p>
                  </div>
                </div>

                <button
                  onClick={() => navigate('/profile')}
                  className="w-full py-3.5 rounded-full bg-gradient-to-r from-[#FECDD3] via-[#F472B6] to-[#E11D48] text-[#2A0314] font-bold text-sm hover:scale-[1.02] transition-transform shadow-xl shadow-[#E11D48]/30"
                  data-testid="memory-locked-upgrade-btn"
                >
                  Unlock Permanent Memory
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Premium/Ultimate users → the regular vault UI */}
      {userTier && userTier !== 'free' && (
        <>
      {/* Header */}
      <header className="sticky top-0 z-30 bg-[#050505]/95 backdrop-blur-sm border-b border-white/5 px-4 h-12 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="text-white" data-testid="memory-back-btn">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2 flex-1">
          <BookHeart className="w-4 h-4 text-[#E11D48]" />
          <h1 className="font-bold text-white font-['Unbounded'] text-sm">Memory Vault</h1>
          {userTier === 'premium' && (
            <span className="text-[9px] uppercase tracking-widest font-bold text-[#F59E0B]">· Basic</span>
          )}
          {userTier === 'ultimate' && (
            <span className="text-[9px] uppercase tracking-widest font-bold text-[#FDA4AF]">· Flawless</span>
          )}
        </div>
        <button
          onClick={() => setAddingOpen(true)}
          className="p-2 -mr-2 text-white"
          data-testid="memory-add-btn"
        >
          <Plus className="w-5 h-5" />
        </button>
      </header>

      {/* Intro card */}
      <div className="mx-4 mt-4 p-3 bg-gradient-to-br from-[#E11D48]/10 to-[#F59E0B]/5 border border-[#E11D48]/20 rounded-2xl">
        <div className="flex items-start gap-2">
          <Sparkles className="w-4 h-4 text-[#E11D48] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-medium text-white">Your characters remember everything</p>
            <p className="text-[11px] text-[#A1A1AA] mt-0.5 leading-relaxed">
              Global memories are known by every character. Character-specific memories are private to that one character.
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="px-4 mt-4 flex gap-2" data-testid="memory-tabs">
        <button
          onClick={() => setTab('global')}
          className={`flex-1 py-2 rounded-full text-xs font-medium transition-colors ${tab === 'global' ? 'bg-white text-black' : 'bg-white/5 text-white/70 hover:text-white'}`}
          data-testid="memory-tab-global"
        >
          Global ({tab === 'global' ? memories.length : '·'})
        </button>
        <button
          onClick={() => { setTab('character'); if (!selectedCharId && characters[0]) setSelectedCharId(characters[0].id); }}
          className={`flex-1 py-2 rounded-full text-xs font-medium transition-colors ${tab === 'character' ? 'bg-white text-black' : 'bg-white/5 text-white/70 hover:text-white'}`}
          data-testid="memory-tab-character"
        >
          Per Character
        </button>
      </div>

      {/* Character selector */}
      {tab === 'character' && (
        <div className="px-4 mt-3">
          {characters.length === 0 ? (
            <p className="text-[11px] text-[#71717A] text-center py-3">Chat with a character first to see character-specific memories.</p>
          ) : (
            <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1" data-testid="memory-char-picker">
              {characters.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setSelectedCharId(c.id)}
                  className={`flex-shrink-0 flex items-center gap-2 px-2.5 py-1.5 rounded-full text-xs transition-colors border ${selectedCharId === c.id ? 'bg-[#E11D48] border-[#E11D48] text-white' : 'bg-white/5 border-white/10 text-white/70'}`}
                  data-testid={`memory-char-${c.id}`}
                >
                  {c.avatar && <img src={c.avatar} alt="" className="w-5 h-5 rounded-full object-cover" />}
                  <span className="truncate max-w-[100px]">{c.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Memory list */}
      <div className="px-4 mt-4 space-y-2" data-testid="memory-list">
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-[#71717A]" /></div>
        ) : memories.length === 0 ? (
          <div className="text-center py-10">
            <BookHeart className="w-10 h-10 text-[#27272A] mx-auto mb-2" />
            <p className="text-sm text-[#71717A]">No memories yet</p>
            <p className="text-[11px] text-[#52525B] mt-1">Keep chatting — your characters will start remembering things naturally.</p>
          </div>
        ) : (
          memories.map((m) => {
            const meta = TYPE_META[m.type] || TYPE_META.fact;
            const isEditing = editingId === m.id;
            return (
              <div key={m.id} className={`p-3 rounded-xl border ${m.pinned ? 'bg-[#E11D48]/10 border-[#E11D48]/20' : 'bg-[#111318] border-white/[0.06]'}`} data-testid={`memory-item-${m.id}`}>
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-1.5 flex-wrap">
                      <span className={`px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider border rounded-full ${meta.cls}`}>{meta.label}</span>
                      {m.pinned && (
                        <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-[#E11D48]/20 text-[#FECDD3] border border-[#E11D48]/30 rounded-full flex items-center gap-1">
                          <Pin className="w-2.5 h-2.5" /> Pinned
                        </span>
                      )}
                    </div>
                    {isEditing ? (
                      <Input
                        value={editingContent}
                        onChange={(e) => setEditingContent(e.target.value.slice(0, 280))}
                        className="bg-[#1A1D24] border-white/10 text-white text-sm rounded-lg"
                        autoFocus
                        data-testid={`memory-edit-input-${m.id}`}
                      />
                    ) : (
                      <p className="text-sm text-white leading-relaxed break-words">{m.content}</p>
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    {isEditing ? (
                      <button onClick={() => saveEdit(m)} className="p-1.5 rounded-md bg-[#E11D48]/15 hover:bg-[#E11D48]/25 text-[#E11D48]" data-testid={`memory-save-${m.id}`}>
                        <Save className="w-3.5 h-3.5" />
                      </button>
                    ) : (
                      <>
                        <button onClick={() => togglePin(m)} className={`p-1.5 rounded-md ${m.pinned ? 'bg-[#E11D48]/20 text-[#E11D48]' : 'bg-white/5 text-[#71717A] hover:text-white'}`} data-testid={`memory-pin-${m.id}`}>
                          <Pin className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => { setEditingId(m.id); setEditingContent(m.content); }} className="p-1.5 rounded-md bg-white/5 text-[#71717A] hover:text-white" data-testid={`memory-edit-${m.id}`}>
                          <Edit3 className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => deleteOne(m)} className="p-1.5 rounded-md bg-white/5 text-[#71717A] hover:text-[#E11D48]" data-testid={`memory-delete-${m.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Clear all */}
      {memories.length > 0 && (
        <div className="px-4 mt-6 mb-4">
          <button
            onClick={clearAll}
            disabled={clearing}
            className="w-full py-2.5 text-xs text-[#E11D48]/80 hover:text-[#E11D48] border border-[#E11D48]/20 rounded-xl disabled:opacity-50"
            data-testid="memory-clear-all-btn"
          >
            {clearing ? 'Clearing...' : 'Clear all (keep pinned)'}
          </button>
        </div>
      )}

      {/* Add memory modal */}
      {addingOpen && (
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center" data-testid="memory-add-modal">
          <div className="w-full sm:max-w-md bg-[#0F1115] border border-white/10 rounded-t-3xl sm:rounded-3xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-['Unbounded'] text-sm text-white">Add a memory</h3>
              <button onClick={() => setAddingOpen(false)} className="p-1.5 text-[#71717A] hover:text-white"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-[11px] text-[#A1A1AA]">
              {tab === 'global' ? 'All characters will know this.' : selectedCharId ? 'Only this character will know this.' : 'Choose a character first.'}
            </p>
            <Input
              value={newContent}
              onChange={(e) => setNewContent(e.target.value.slice(0, 280))}
              placeholder="e.g. I work at a coffee shop in Manchester"
              className="bg-[#1A1D24] border-white/10 text-white rounded-lg"
              data-testid="memory-new-input"
            />
            <p className="text-[10px] text-[#52525B] text-right">{newContent.length}/280</p>
            <Button
              onClick={addMemory}
              disabled={!newContent.trim() || submitting || (tab === 'character' && !selectedCharId)}
              className="w-full bg-[#E11D48] hover:bg-[#BE123C] text-white rounded-full"
              data-testid="memory-submit-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Save & Pin
            </Button>
          </div>
        </div>
      )}
        </>
      )}
      </div>
    </div>
  );
}
